// Capture: the viewport's frame plus the HTML labels drawn over it, as a PNG download. The rest
// of the UI is left out, so a capture is a clean image of the scene.

import { getViewport } from '../viewport/host.ts';
import { useAtlas } from '../app/store.ts';

async function blobToImage(b: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(b);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export async function captureFrame(): Promise<Blob> {
  const vp = await getViewport();
  if (!vp) throw new Error('no viewport');
  const { png } = await vp.send({ cmd: 'capture' });
  const frame = await blobToImage(png);
  const canvas = document.createElement('canvas');
  canvas.width = frame.naturalWidth;
  canvas.height = frame.naturalHeight;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(frame, 0, 0);
  const root = document.querySelector<HTMLElement>('.labels');
  if (root && root.style.opacity !== '0') {
    const base = root.getBoundingClientRect();
    const k = canvas.width / Math.max(1, base.width);
    ctx.scale(k, k);
    ctx.strokeStyle = 'rgba(255,255,255,0.42)';
    ctx.lineWidth = 1;
    for (const line of root.querySelectorAll('line')) {
      ctx.beginPath();
      ctx.moveTo(Number(line.getAttribute('x1')), Number(line.getAttribute('y1')));
      ctx.lineTo(Number(line.getAttribute('x2')), Number(line.getAttribute('y2')));
      ctx.stroke();
    }
    ctx.fillStyle = '#fff';
    for (const dot of root.querySelectorAll('circle')) {
      ctx.beginPath();
      ctx.arc(Number(dot.getAttribute('cx')), Number(dot.getAttribute('cy')), 2.25, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.font = '500 12px Inter, system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    for (const el of root.querySelectorAll<HTMLElement>('.label')) {
      const r = el.getBoundingClientRect();
      const x = r.left - base.left, y = r.top - base.top;
      const selected = el.dataset.focus === 'selected';
      roundRect(ctx, x, y, r.width, r.height, 6);
      ctx.fillStyle = selected ? '#eef2f7' : 'rgba(9,11,15,0.78)';
      ctx.fill();
      ctx.fillStyle = selected ? '#0b0e13' : '#eef2f7';
      ctx.fillText(el.textContent ?? '', x + 9, y + r.height / 2 + 0.5);
    }
  }
  return new Promise((ok, err) => canvas.toBlob((b) => (b ? ok(b) : err(new Error('encode failed'))), 'image/png'));
}

export async function downloadCapture() {
  const blob = await captureFrame();
  const s = useAtlas.getState();
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `brain-atlas-${s.scenario}-${stamp}.png`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
